/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, useEffect, useMemo, useState} from 'react'
import {Modal, ModalHeader} from 'react-bootstrap'
import LoadingComponent from '../../../../components/LoadingComponent/LoadingComponent'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import notifyToast from '../../../../components/Toast/Toast'
import {zavrsiProizvodnjuRolne} from '../../../../api/rolneProizvodnju'
import {RolneZaProizvodnjuModel} from '../../../../models/RolneZaProizvodnju'
import clsx from 'clsx'
import LokacijeRepromaterijalaDropdown from '../../../../components/LokacijeRepromaterijalaDropdown/LokacijeRepromaterijalaDropdown'
import LokacijeRsTraka from '../../../../components/LokacijeRSTraka/LokacijeRsTraka'
import {ToastContainer} from 'react-toastify'

interface Props {
    isModalOpen: boolean
    setisModalOpen: (v: boolean) => void
    data: RolneZaProizvodnjuModel
    update: () => void
}

const KrajRasjecanjeModal: FC<Props> = ({isModalOpen, setisModalOpen, data, update}) => {
    const [loading, setLoading] = useState(false)
    const [orginalnaRolna, setOrginalnaRolna] = useState<string>('') // '' | 'DA' | 'NE'

    const initialValues = useMemo(
        () => ({
            rolnaId: data?.Id,
            duzina: '' as any,
            tezina: '' as any,
            lokacija: '' as any,
            lokacijaTrake: '' as any,
            napomena: '',
            rolnaPotrosena: '' as any, // '' | 'DA' | 'NE'
            krajnjaTezinaRolne: '' as any,
        }),
        [data?.Id]
    )

    const validationSchema = Yup.object({
        duzina: Yup.number().typeError('Unesite broj').required('Obavezno polje'),
        tezina: Yup.number().typeError('Unesite broj').required('Obavezno polje'),
        lokacija: Yup.string().required('Obavezno polje'),
        lokacijaTrake: Yup.string().required('Obavezno polje'),
    })

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, {setStatus, setSubmitting, resetForm}) => {
            setLoading(true)
            try {
                const payload = {
                    ...values,
                    duzina: values.duzina === '' ? null : Number(values.duzina),
                    tezina: values.tezina === '' ? null : Number(values.tezina),
                    krajnjaTezinaRolne:
                        values.krajnjaTezinaRolne === '' || values.krajnjaTezinaRolne === undefined
                            ? null
                            : Number(values.krajnjaTezinaRolne),
                }

                const response = await zavrsiProizvodnjuRolne(payload as any)

                if (response?.error === 0) {
                    notifyToast(true, response.message)
                    update()
                    setisModalOpen(false)
                    setOrginalnaRolna('')
                    resetForm()
                } else {
                    notifyToast(false, response?.message || 'An unexpected error occurred.')
                }
            } catch (error: any) {
                console.error('Error: ', error?.response?.data?.message ?? error)
                notifyToast(false, error?.response?.data?.message ?? 'Greška')
                setStatus(error)
            } finally {
                setLoading(false)
                setSubmitting(false)
            }
        },
    })

    // Clear original-roll fields when user doesn't want to “prometovati”
    useEffect(() => {
        if (orginalnaRolna !== 'DA') {
            formik.setFieldValue('rolnaPotrosena', '')
            formik.setFieldValue('krajnjaTezinaRolne', '')
        }
    }, [orginalnaRolna])

    // If consumed => remaining weight not needed
    useEffect(() => {
        if (formik.values.rolnaPotrosena !== 'NE') {
            formik.setFieldValue('krajnjaTezinaRolne', '')
        }
    }, [formik.values.rolnaPotrosena])

    const disableSubmit = loading || formik.isSubmitting || !formik.isValid

    const FieldError: FC<{name: string}> = ({name}) => {
        const touched = (formik.touched as any)[name]
        const err = (formik.errors as any)[name]
        if (!touched || !err) return null
        return <div className='text-danger small mt-1'>{String(err)}</div>
    }

    const summary = useMemo(() => {
        const lokR = formik.values.lokacija ? String(formik.values.lokacija) : '—'
        const lokT = formik.values.lokacijaTrake ? String(formik.values.lokacijaTrake) : '—'

        const org = orginalnaRolna === 'DA' ? 'DA' : orginalnaRolna === 'NE' ? 'NE' : '—'
        const pot = formik.values.rolnaPotrosena
            ? formik.values.rolnaPotrosena === 'DA'
                ? 'POTROŠENA'
                : 'NIJE POTROŠENA'
            : '—'
        const preostalo =
            formik.values.rolnaPotrosena === 'NE' && formik.values.krajnjaTezinaRolne
                ? String(formik.values.krajnjaTezinaRolne)
                : '—'

        return [
            {label: 'RŠ (ID)', value: String(formik.values.rolnaId ?? '—')},
            {label: 'Broj rolne', value: String(data?.BrojRolne ?? '—')},
            {label: 'Dužina', value: formik.values.duzina ? `${formik.values.duzina} m` : '—'},
            {label: 'Težina', value: formik.values.tezina ? `${formik.values.tezina} kg` : '—'},
            {label: 'Lokacija rolne', value: lokR},
            {label: 'Lokacija trake', value: lokT},
            {label: 'Prometuj org. rolnu', value: org},
            {label: 'Status org. rolne', value: pot},
            {label: 'Preostalo (kg)', value: preostalo},
        ]
    }, [
        data?.BrojRolne,
        formik.values.duzina,
        formik.values.tezina,
        formik.values.lokacija,
        formik.values.lokacijaTrake,
        formik.values.rolnaId,
        formik.values.rolnaPotrosena,
        formik.values.krajnjaTezinaRolne,
        orginalnaRolna,
    ])

    return (
        <>
            {/* ✅ FIXED MODAL LAYOUT (no overflow outside modal) */}
            <style>{`
        .rs-modal .modal-dialog{
          margin: 1rem auto;
          max-height: calc(100dvh - 2rem);
        }

        .rs-modal .modal-content{
          max-height: calc(100dvh - 2rem);
          display: flex;
          flex-direction: column;
          overflow: hidden; /* prevents “going out” */
        }

        /* form must be a flex column and allow shrinking */
        .rs-modal form{
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0; /* CRITICAL */
        }

        /* body becomes scroll container and can shrink */
        .rs-modal .modal-body{
          flex: 1 1 auto;
          min-height: 0;  /* CRITICAL */
          overflow-y: auto;
        }

        .rs-modal .modal-header,
        .rs-modal .modal-footer{
          flex: 0 0 auto;
        }
      `}</style>

            <Modal
                show={isModalOpen}
                onHide={() => setisModalOpen(false)}
                container={document.body}
                className='rs-modal'
                dialogClassName='modal-xl'
                contentClassName='border-0 shadow'
                centered={false}
                backdrop='static'
            >
                <ToastContainer />

                <ModalHeader closeButton className='py-3'>
                    <div className='d-flex flex-column'>
                        <div className='fs-3 fw-bolder text-dark'>Kreiraj RS</div>
                        <div className='text-muted small'>Kraj rasjecanja</div>
                    </div>
                </ModalHeader>

                {loading ? (
                    <div className='p-6'>
                        <LoadingComponent />
                    </div>
                ) : (
                    <form onSubmit={formik.handleSubmit} noValidate>
                        {/* BODY: scrolls INSIDE modal */}
                        <div className='modal-body p-3 p-md-5'>
                            {/* ROW 1: Identifikacija | RS parametri */}
                            <div className='row g-4 mb-4'>
                                <div className='col-12 col-lg-6'>
                                    <div className='card border-0 shadow-sm h-100'>
                                        <div className='card-body p-3 p-md-4'>
                                            <div className='fw-bold text-dark mb-1'>Identifikacija</div>
                                            <div className='text-muted small mb-3'>Podaci o RS-u</div>

                                            <div className='row g-3'>
                                                <div className='col-12 col-md-6'>
                                                    <label className='form-label fw-semibold'>RS BR</label>
                                                    <input
                                                        type='number'
                                                        value={String(formik.values.rolnaId ?? '')}
                                                        className='form-control form-control-lg form-control-solid'
                                                        disabled
                                                    />
                                                </div>

                                                <div className='col-12 col-md-6'>
                                                    <label className='form-label fw-semibold'>BROJ ROLNE</label>
                                                    <input
                                                        type='number'
                                                        value={String(data?.BrojRolne ?? '')}
                                                        className='form-control form-control-lg form-control-solid'
                                                        disabled
                                                    />
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label fw-semibold'>TRENUTNA TEŽINA ROLNE (kg)</label>
                                                    <input
                                                        type='number'
                                                        value={String(data?.repromaterijal?.ReprMatTrenutnaKolicina ?? '')}
                                                        className='form-control form-control-lg form-control-solid'
                                                        disabled
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className='col-12 col-lg-6'>
                                    <div className='card border-0 shadow-sm h-100'>
                                        <div className='card-body p-3 p-md-4'>
                                            <div className='fw-bold text-dark mb-1'>RŠ parametri</div>
                                            <div className='text-muted small mb-3'>Unesite dužinu i težinu RŠ</div>

                                            <div className='row g-3'>
                                                <div className='col-12 col-md-6'>
                                                    <label className='form-label fw-semibold required'>Dužina RŠ-a (m)</label>
                                                    <input
                                                        type='number'
                                                        placeholder='npr. 125'
                                                        {...formik.getFieldProps('duzina')}
                                                        className={clsx('form-control form-control-lg form-control-solid', {
                                                            'is-invalid': formik.touched.duzina && formik.errors.duzina,
                                                            'is-valid': formik.touched.duzina && !formik.errors.duzina,
                                                        })}
                                                    />
                                                    <FieldError name='duzina' />
                                                </div>

                                                <div className='col-12 col-md-6'>
                                                    <label className='form-label fw-semibold required'>Težina RŠ-a (kg)</label>
                                                    <input
                                                        type='number'
                                                        placeholder='npr. 980'
                                                        {...formik.getFieldProps('tezina')}
                                                        className={clsx('form-control form-control-lg form-control-solid', {
                                                            'is-invalid': formik.touched.tezina && formik.errors.tezina,
                                                            'is-valid': formik.touched.tezina && !formik.errors.tezina,
                                                        })}
                                                    />
                                                    <FieldError name='tezina' />
                                                </div>

                                                <div className='col-12'>
                                                    <div className='alert alert-light border mb-0 py-2'>
                            <span className='text-muted small'>
                              Nakon unosa parametara, obavezno odaberite lokacije ispod.
                            </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ROW 2: Lokacije */}
                            <div className='row g-4 mb-4'>
                                <div className='col-12 col-lg-6'>
                                    <div className='card border-0 shadow-sm h-100'>
                                        <div className='card-body p-3 p-md-4'>
                                            <div className='fw-bold text-dark mb-1'>Lokacija rolne</div>
                                            <div className='text-muted small mb-3'>Gdje se skladišti rolna</div>

                                            <label className='form-label fw-semibold required'>Odaberite lokaciju rolne</label>
                                            <LokacijeRepromaterijalaDropdown
                                                value={formik.values.lokacija}
                                                onChange={(name, value) => formik.setFieldValue('lokacija', value)}
                                                errors={formik.errors.lokacija as any}
                                                touched={formik.touched.lokacija as any}
                                                selectName='lokacija'
                                            />
                                            <FieldError name='lokacija' />
                                        </div>
                                    </div>
                                </div>

                                <div className='col-12 col-lg-6'>
                                    <div className='card border-0 shadow-sm h-100'>
                                        <div className='card-body p-3 p-md-4'>
                                            <div className='fw-bold text-dark mb-1'>Lokacija trake</div>
                                            <div className='text-muted small mb-3'>Gdje se skladišti traka</div>

                                            <label className='form-label fw-semibold required'>Odaberite lokaciju trake</label>
                                            <LokacijeRsTraka
                                                value={formik.values.lokacijaTrake}
                                                onChange={(name, value) => formik.setFieldValue('lokacijaTrake', value)}
                                                errors={formik.errors.lokacijaTrake as any}
                                                touched={formik.touched.lokacijaTrake as any}
                                                selectName='lokacijaTrake'
                                            />
                                            <FieldError name='lokacijaTrake' />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ROW 3: Originalna rolna */}
                            <div className='row g-4'>
                                <div className='col-12'>
                                    <div className='card border-0 shadow-sm'>
                                        <div className='card-body p-3 p-md-4'>
                                            <div className='d-flex align-items-start justify-content-between gap-2 flex-wrap'>
                                                <div>
                                                    <div className='fw-bold text-dark mb-1'>Originalna rolna</div>
                                                    <div className='text-muted small'>
                                                        Ako želite, prometujte originalnu rolnu i postavite status potrošnje.
                                                    </div>
                                                </div>
                                                <span className='badge badge-light-primary'>Opcionalno</span>
                                            </div>

                                            <div className='separator my-3' />

                                            <div className='row g-3 align-items-end'>
                                                <div className='col-12 col-md-4'>
                                                    <label className='form-label fw-semibold'>Prometuj originalnu rolnu</label>
                                                    <select
                                                        className='form-select form-select-lg form-select-solid'
                                                        value={orginalnaRolna}
                                                        onChange={(e) => setOrginalnaRolna(e.target.value)}
                                                    >
                                                        <option value=''>Odaberi...</option>
                                                        <option value='NE'>Ne</option>
                                                        <option value='DA'>Da</option>
                                                    </select>
                                                </div>

                                                {orginalnaRolna === 'DA' && (
                                                    <div className='col-12 col-md-4'>
                                                        <label className='form-label fw-semibold'>Originalna rolna potrošena</label>
                                                        <select
                                                            {...formik.getFieldProps('rolnaPotrosena')}
                                                            className={clsx('form-select form-select-lg form-select-solid', {
                                                                'is-invalid': formik.touched.rolnaPotrosena && formik.errors.rolnaPotrosena,
                                                                'is-valid': formik.touched.rolnaPotrosena && !formik.errors.rolnaPotrosena,
                                                            })}
                                                        >
                                                            <option value=''>Odaberi...</option>
                                                            <option value='NE'>Ne</option>
                                                            <option value='DA'>Da</option>
                                                        </select>
                                                        <FieldError name='rolnaPotrosena' />
                                                    </div>
                                                )}

                                                {orginalnaRolna === 'DA' && formik.values.rolnaPotrosena && (
                                                    <div className='col-12'>
                                                        <div className='alert alert-success border mb-0'>
                                                            <div className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
                                                                <div
                                                                    className='fw-semibold text-dark px-3 py-2 rounded border'
                                                                    style={{borderColor: 'rgba(0,0,0,.15)'}}
                                                                >
                                                                    Status:{' '}
                                                                    {formik.values.rolnaPotrosena === 'DA' ? 'POTROŠENA' : 'NIJE POTROŠENA'}
                                                                </div>
                                                                <span
                                                                    className={clsx(
                                                                        'badge',
                                                                        formik.values.rolnaPotrosena === 'DA'
                                                                            ? 'badge-light-success'
                                                                            : 'badge-light-warning'
                                                                    )}
                                                                >
                                  {formik.values.rolnaPotrosena === 'DA' ? 'DA' : 'NE'}
                                </span>
                                                            </div>

                                                            {formik.values.rolnaPotrosena === 'DA' ? (
                                                                <div className='text-muted small mt-2'>
                                                                    Ako je potrošena, ne unosite preostalu količinu.
                                                                </div>
                                                            ) : (
                                                                <div className='text-muted small mt-2'>
                                                                    Pošto rolna nije potrošena, unesite preostalu količinu (kg) ispod.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {orginalnaRolna === 'DA' && formik.values.rolnaPotrosena === 'NE' && (
                                                    <div className='col-12 col-md-6'>
                                                        <h3 className='text-danger fw-semibold mt-3 mb-3'>
                                                            Preostala količina originalne rolne (kg)
                                                        </h3>
                                                        <input
                                                            type='number'
                                                            placeholder='npr. 120'
                                                            {...formik.getFieldProps('krajnjaTezinaRolne')}
                                                            className={clsx('form-control form-control-lg form-control-solid', {
                                                                'is-invalid':
                                                                    formik.touched.krajnjaTezinaRolne && formik.errors.krajnjaTezinaRolne,
                                                                'is-valid':
                                                                    formik.touched.krajnjaTezinaRolne && !formik.errors.krajnjaTezinaRolne,
                                                            })}
                                                        />
                                                        <FieldError name='krajnjaTezinaRolne' />
                                                    </div>
                                                )}
                                            </div>

                                            <div className='separator mt-6 mb-6' />

                                            <div className='row'>
                                                <div className='col-12 col-md-6'>
                                                    <label className='form-label fw-semibold'>Napomena</label>
                                                    <textarea
                                                        rows={2}
                                                        placeholder='Upišite napomenu...'
                                                        {...formik.getFieldProps('napomena')}
                                                        className={clsx('form-control form-control-lg form-control-solid', {
                                                            'is-invalid': formik.touched.napomena && formik.errors.napomena,
                                                            'is-valid': formik.touched.napomena && !formik.errors.napomena,
                                                        })}
                                                    />
                                                    <FieldError name='napomena' />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* FOOTER: fixed (always visible) */}
                        <div className='modal-footer border-top d-flex flex-column flex-lg-row gap-3 align-items-stretch align-items-lg-center justify-content-between'>
                            {/* Summary */}
                            <div className='w-100 w-lg-auto flex-grow-1'>
                                <div className='p-3 border rounded bg-light'>
                                    <div className='fw-bold text-dark mb-2'>
                                        <i className='bi bi-list-check me-2' />
                                        Sažetak (šta potvrđujete)
                                    </div>

                                    <div className='row g-2 small'>
                                        {summary.map((item) => (
                                            <div key={item.label} className='col-12 col-sm-6'>
                                                <div className='d-flex justify-content-between gap-2 border-bottom pb-1'>
                                                    <span className='text-muted'>{item.label}</span>
                                                    <span className='fw-semibold text-end'>{item.value}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className='d-flex w-100 justify-content-between align-items-center'>
                                {/* LEFT */}
                                <button
                                    type='button'
                                    className='btn btn-light btn-lg'
                                    onClick={() => setisModalOpen(false)}
                                    disabled={loading || formik.isSubmitting}
                                >
                                    <i className='bi bi-x-lg me-2' />
                                    Odustani
                                </button>

                                {/* RIGHT */}
                                <button
                                    type='submit'
                                    className='btn btn-primary btn-lg'
                                    disabled={disableSubmit}
                                >
                                    {!loading ? (
                                        <>
                                            <i className='bi bi-check2-circle me-2' />
                                            Potvrdi
                                        </>
                                    ) : (
                                        <span className='d-inline-flex align-items-center gap-2'>
                                            Please wait...
                                            <span className='spinner-border spinner-border-sm align-middle' />
                                          </span>
                                    )}
                                </button>
                            </div>

                        </div>
                    </form>
                )}
            </Modal>
        </>
    )
}

export default KrajRasjecanjeModal
